/**
 * Premium Product Page JavaScript
 * Handles interactive components for the premium product template
 */

(function() {
  'use strict';

  // Utility: Debounce function
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Countdown Timer
  class OfferTimer {
    constructor(element) {
      this.element = element;
      this.endTime = this.getEndOfDay();
      this.init();
    }

    getEndOfDay() {
      const now = new Date();
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);
      return endOfDay.getTime();
    }

    init() {
      this.update();
      this.interval = setInterval(() => this.update(), 1000);
    }

    update() {
      const now = new Date().getTime();
      const distance = this.endTime - now;

      if (distance < 0) {
        clearInterval(this.interval);
        this.element.textContent = 'OFFER EXPIRED';
        return;
      }

      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      const timeString = `OFFER ENDS IN ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      
      const textSpan = this.element.querySelector('span');
      if (textSpan) {
        textSpan.textContent = timeString;
      }
    }

    destroy() {
      clearInterval(this.interval);
    }
  }

  // Gallery Component
  class ProductGallery {
    constructor(container) {
      this.container = container;
      this.mainImage = container.querySelector('#main-product-image');
      this.thumbnails = container.querySelectorAll('.product-gallery__thumbnail');
      this.currentIndex = 0;
      
      this.init();
    }

    init() {
      this.thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.setActive(index));
      });

      // Touch swipe support
      this.setupSwipe();
    }

    setActive(index) {
      this.currentIndex = index;
      
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('active', i === index);
      });

      const activeThumb = this.thumbnails[index];
      if (activeThumb && this.mainImage) {
        const newSrc = activeThumb.dataset.imageUrl;
        if (newSrc) {
          // Fade transition
          this.mainImage.style.opacity = '0.5';
          setTimeout(() => {
            this.mainImage.src = newSrc;
            this.mainImage.style.opacity = '1';
          }, 150);
        }
      }
    }

    setupSwipe() {
      const mainWrapper = this.container.querySelector('.product-gallery__main');
      if (!mainWrapper) return;

      let touchStartX = 0;
      let touchEndX = 0;

      mainWrapper.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      mainWrapper.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX);
      }, { passive: true });
    }

    handleSwipe(startX, endX) {
      const threshold = 50;
      const diff = startX - endX;

      if (Math.abs(diff) > threshold) {
        if (diff > 0 && this.currentIndex < this.thumbnails.length - 1) {
          // Swipe left - next
          this.setActive(this.currentIndex + 1);
        } else if (diff < 0 && this.currentIndex > 0) {
          // Swipe right - previous
          this.setActive(this.currentIndex - 1);
        }
      }
    }
  }

  // Bundle Selector
  class BundleSelector {
    constructor(container) {
      this.container = container;
      this.bundles = container.querySelectorAll('.product-bundle');
      this.form = document.querySelector('form[id^="product-form"]');
      
      this.init();
    }

    init() {
      this.bundles.forEach(bundle => {
        bundle.addEventListener('click', () => this.selectBundle(bundle));
      });
    }

    selectBundle(selectedBundle) {
      this.bundles.forEach(bundle => {
        bundle.classList.remove('selected');
        bundle.setAttribute('aria-selected', 'false');
      });

      selectedBundle.classList.add('selected');
      selectedBundle.setAttribute('aria-selected', 'true');

      // Update price display if needed
      const price = selectedBundle.dataset.price;
      const comparePrice = selectedBundle.dataset.comparePrice;
      
      // Dispatch custom event for other components
      this.container.dispatchEvent(new CustomEvent('bundle:selected', {
        detail: { 
          bundleId: selectedBundle.dataset.bundleId,
          price,
          comparePrice
        },
        bubbles: true
      }));
    }
  }

  // Subscription Toggle
  class SubscriptionToggle {
    constructor(container) {
      this.container = container;
      this.toggle = container.querySelector('.product-subscription__toggle');
      this.isActive = false;
      
      this.init();
    }

    init() {
      if (!this.toggle) return;

      this.toggle.addEventListener('click', () => this.toggleSubscription());
      this.toggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleSubscription();
        }
      });
    }

    toggleSubscription() {
      this.isActive = !this.isActive;
      this.toggle.classList.toggle('active', this.isActive);
      this.toggle.setAttribute('aria-checked', this.isActive.toString());

      // Dispatch custom event
      this.container.dispatchEvent(new CustomEvent('subscription:toggled', {
        detail: { isSubscription: this.isActive },
        bubbles: true
      }));
    }
  }

  // FAQ Accordion
  class FAQAccordion {
    constructor(container, options = {}) {
      this.container = container;
      this.items = container.querySelectorAll('[data-faq-item]');
      this.singleOpen = options.singleOpen !== false;
      
      this.init();
    }

    init() {
      this.items.forEach(item => {
        const button = item.querySelector('.faq__question');
        if (button) {
          button.addEventListener('click', () => this.toggleItem(item));
        }
      });
    }

    toggleItem(item) {
      const isOpen = item.classList.contains('open');

      if (this.singleOpen) {
        this.items.forEach(i => {
          i.classList.remove('open');
          const btn = i.querySelector('.faq__question');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
      }

      if (!isOpen) {
        item.classList.add('open');
        item.querySelector('.faq__question')?.setAttribute('aria-expanded', 'true');
      } else {
        item.classList.remove('open');
        item.querySelector('.faq__question')?.setAttribute('aria-expanded', 'false');
      }
    }
  }

  // Scroll Animations
  class ScrollAnimations {
    constructor() {
      this.observerOptions = {
        root: null,
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.1
      };
      
      this.init();
    }

    init() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.observer.unobserve(entry.target);
          }
        });
      }, this.observerOptions);

      // Observe all premium sections
      document.querySelectorAll('.premium-section').forEach(section => {
        section.classList.add('animate-on-scroll');
        this.observer.observe(section);
      });
    }
  }

  // Sticky Add to Cart (optional enhancement)
  class StickyATC {
    constructor() {
      this.hero = document.querySelector('.product-hero');
      this.stickyBar = null;
      this.isVisible = false;
      
      this.init();
    }

    init() {
      if (!this.hero) return;

      this.createStickyBar();
      this.setupObserver();
    }

    createStickyBar() {
      const productTitle = document.querySelector('.product-info__title');
      const atcButton = document.querySelector('.product-atc__button');
      
      if (!productTitle || !atcButton) return;

      this.stickyBar = document.createElement('div');
      this.stickyBar.className = 'sticky-atc';
      this.stickyBar.innerHTML = `
        <div class="sticky-atc__content">
          <span class="sticky-atc__title">${productTitle.textContent}</span>
          <button type="button" class="sticky-atc__button premium-btn premium-btn--primary">
            Add To Cart
          </button>
        </div>
      `;
      
      this.stickyBar.querySelector('.sticky-atc__button').addEventListener('click', () => {
        atcButton.click();
      });

      document.body.appendChild(this.stickyBar);
    }

    setupObserver() {
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const shouldShow = !entry.isIntersecting;
          this.stickyBar?.classList.toggle('is-visible', shouldShow);
        });
      }, {
        threshold: 0,
        rootMargin: '-100px 0px 0px 0px'
      });

      const atcSection = document.querySelector('.product-atc');
      if (atcSection) {
        observer.observe(atcSection);
      }
    }
  }

  // Initialize all components
  function initPremiumProduct() {
    // Gallery
    const galleryContainer = document.querySelector('.product-gallery');
    if (galleryContainer) {
      new ProductGallery(galleryContainer);
    }

    // Offer Timer
    const timerElements = document.querySelectorAll('.product-offer-timer');
    timerElements.forEach(el => new OfferTimer(el));

    // Bundle Selectors
    const bundleContainers = document.querySelectorAll('.product-bundles');
    bundleContainers.forEach(container => new BundleSelector(container));

    // Subscription Toggles
    const subscriptionContainers = document.querySelectorAll('.product-subscription');
    subscriptionContainers.forEach(container => new SubscriptionToggle(container));

    // FAQ Accordions (if not handled by section JS)
    const faqLists = document.querySelectorAll('.faq__list:not([data-initialized])');
    faqLists.forEach(list => {
      list.setAttribute('data-initialized', 'true');
      new FAQAccordion(list);
    });

    // Scroll Animations
    new ScrollAnimations();

    // Sticky ATC (optional - uncomment to enable)
    // new StickyATC();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPremiumProduct);
  } else {
    initPremiumProduct();
  }

  // Re-initialize on Shopify section load (for Theme Editor)
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.contains('premium-section') || 
        event.target.querySelector('.premium-section')) {
      initPremiumProduct();
    }
  });

})();
